import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { uploadPhoto } from '../utils/firestoreService';
import { useAuth } from '../hooks/useAuth';

const F = "'Inter',-apple-system,sans-serif";
const D = "'Bangers','Arial Black',cursive";
const O = 'var(--orange)';

const numSort = (a, b) => {
  const na = parseFloat(a.numero), nb = parseFloat(b.numero);
  if (!isNaN(na) && !isNaN(nb) && na !== nb) return na - nb;
  if (!isNaN(na) && isNaN(nb)) return -1;
  if (isNaN(na) && !isNaN(nb)) return 1;
  return (a.titre || '').localeCompare(b.titre || '');
};
const label = bd => (bd.numero ? `Tome ${bd.numero}` : 'Hors-série') + (bd.titre && bd.titre !== bd.serie ? ` · ${bd.titre}` : '');

export default function PhotoBatchModal({ bds, initialSerie = '', onClose, onToast }) {
  const { user } = useAuth();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const mounted = useRef(true);

  const [serie, setSerie] = useState(initialSerie);
  const [onlyEmpty, setOnlyEmpty] = useState(true);
  const [queue, setQueue] = useState(null);   // null = écran de choix
  const [idx, setIdx] = useState(0);
  const [auto, setAuto] = useState(true);
  const [camOk, setCamOk] = useState(false);
  const [camErr, setCamErr] = useState('');
  const [flash, setFlash] = useState(false);
  const [taken, setTaken] = useState({});      // id -> nb de photos prises dans cette session
  const [pending, setPending] = useState(0);
  const [failed, setFailed] = useState(0);

  const series = useMemo(() => {
    const m = {};
    bds.forEach(b => {
      const k = b.serie?.trim() || '';
      if (!m[k]) m[k] = { total: 0, sans: 0 };
      m[k].total++;
      if (!b.photos?.length) m[k].sans++;
    });
    return Object.entries(m).sort((a, b) => (a[0] || '~').localeCompare(b[0] || '~', 'fr'));
  }, [bds]);

  const candidates = useMemo(() => bds
    .filter(b => serie === '__all__' ? true : (b.serie?.trim() || '') === serie)
    .filter(b => !onlyEmpty || !b.photos?.length)
    .sort((a, b) => serie === '__all__' ? ((a.serie || '').localeCompare(b.serie || '', 'fr') || numSort(a, b)) : numSort(a, b)),
    [bds, serie, onlyEmpty]);

  const stopCam = useCallback(() => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; stopCam(); }; }, [stopCam]);

  // Démarre la caméra une fois l'écran de prise de vue affiché
  const shooting = queue !== null && idx < queue.length;
  useEffect(() => {
    if (!shooting || streamRef.current) return;
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1600 }, height: { ideal: 1600 } }, audio: false,
        });
        if (!mounted.current) { s.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = s;
        if (videoRef.current) { videoRef.current.srcObject = s; await videoRef.current.play().catch(() => {}); }
        setCamOk(true);
      } catch {
        setCamErr("Caméra directe indisponible : le bouton ouvrira l'appareil photo du téléphone.");
      }
    })();
  }, [shooting]);

  const start = () => {
    if (!candidates.length) return;
    setQueue(candidates.map(b => ({ id: b.id, label: label(b), serie: b.serie || '' })));
    setIdx(0);
  };

  const upload = (bdId, file) => {
    setPending(p => p + 1);
    uploadPhoto(user.uid, bdId, file)
      .catch(() => { if (mounted.current) setFailed(f => f + 1); })
      .finally(() => { if (mounted.current) setPending(p => p - 1); });
    setTaken(t => ({ ...t, [bdId]: (t[bdId] || 0) + 1 }));
    if (auto) setIdx(i => i + 1);
  };

  const shoot = () => {
    const cur = queue[idx];
    const vid = videoRef.current;
    if (camOk && vid && vid.videoWidth) {
      setFlash(true); setTimeout(() => setFlash(false), 120);
      if (navigator.vibrate) navigator.vibrate(30);
      const c = document.createElement('canvas');
      c.width = vid.videoWidth; c.height = vid.videoHeight;
      c.getContext('2d').drawImage(vid, 0, 0);
      c.toBlob(b => { if (b) upload(cur.id, b); }, 'image/jpeg', 0.9);
    } else {
      fileRef.current?.click();
    }
  };

  const onFile = e => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (f) upload(queue[idx].id, f);
  };

  const close = () => {
    stopCam();
    const n = Object.values(taken).reduce((a, b) => a + b, 0);
    if (n) onToast?.(`${n} photo${n > 1 ? 's' : ''} ajoutée${n > 1 ? 's' : ''} 📷`);
    onClose();
  };

  const wrap = { position: 'fixed', inset: 0, zIndex: 1000, background: '#000', display: 'flex', flexDirection: 'column', fontFamily: F };
  const nPhotos = Object.values(taken).reduce((a, b) => a + b, 0);

  // ── Écran de choix ──────────────────────────────────────────────
  if (queue === null) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', fontFamily: F }} onClick={close}>
        <div onClick={e => e.stopPropagation()} style={{ background: 'var(--surface,#fff)', color: 'var(--ink,#1C1612)', width: '100%', maxWidth: 480, borderRadius: '18px 18px 0 0', padding: '20px 16px calc(20px + env(safe-area-inset-bottom))' }}>
          <div style={{ fontFamily: D, fontSize: '1.6rem', letterSpacing: '.04em', marginBottom: 4 }}>📸 Photos en série</div>
          <p style={{ margin: '0 0 14px', fontSize: 14, color: 'var(--muted,#8A7E72)' }}>La caméra reste ouverte : un appui = une photo, et on passe tout de suite à l'album suivant.</p>
          <label style={{ fontSize: 13, fontWeight: 600 }}>Quelle série ?</label>
          <select value={serie} onChange={e => setSerie(e.target.value)}
            style={{ width: '100%', fontSize: 16, padding: '10px', margin: '6px 0 12px', borderRadius: 10, border: '1px solid var(--border,#EAE4DC)', background: 'var(--surface2,#fff)', color: 'inherit' }}>
            <option value="" disabled={!series.find(s => s[0] === '')}>— Choisir —</option>
            <option value="__all__">Toute ma collection</option>
            {series.map(([k, v]) => <option key={k || '_'} value={k}>{(k || 'Sans série')} — {v.sans} sans photo / {v.total}</option>)}
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, marginBottom: 16 }}>
            <input type="checkbox" checked={onlyEmpty} onChange={e => setOnlyEmpty(e.target.checked)} style={{ width: 18, height: 18 }} />
            Seulement les albums sans photo
          </label>
          <button onClick={start} disabled={!candidates.length || (serie === '' && !series.find(s => s[0] === ''))}
            style={{ width: '100%', padding: '14px', border: 'none', borderRadius: 12, background: O, color: '#fff', fontSize: 16, fontWeight: 700, opacity: candidates.length ? 1 : .4, cursor: 'pointer' }}>
            {candidates.length ? `Commencer (${candidates.length} album${candidates.length > 1 ? 's' : ''})` : 'Aucun album à photographier'}
          </button>
          <button onClick={close} style={{ width: '100%', marginTop: 8, padding: 12, border: 'none', background: 'none', color: 'var(--muted,#8A7E72)', fontSize: 15, cursor: 'pointer' }}>Annuler</button>
        </div>
      </div>
    );
  }

  // ── Terminé ─────────────────────────────────────────────────────
  if (!shooting) {
    return (
      <div style={{ ...wrap, background: 'var(--bg,#FDFAF6)', color: 'var(--ink,#1C1612)', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 24 }}>
        <div style={{ fontSize: 56 }}>🎉</div>
        <div style={{ fontFamily: D, fontSize: '2rem', letterSpacing: '.04em' }}>Série terminée !</div>
        <p style={{ fontSize: 15, color: 'var(--muted,#8A7E72)' }}>{nPhotos} photo{nPhotos > 1 ? 's' : ''} prise{nPhotos > 1 ? 's' : ''}.
          {pending > 0 && ` Envoi en cours (${pending})…`}{failed > 0 && ` ⚠️ ${failed} échec${failed > 1 ? 's' : ''}.`}</p>
        <button onClick={() => setIdx(i => Math.max(0, i - 1))} style={{ padding: '12px 20px', borderRadius: 12, border: '1px solid var(--border,#EAE4DC)', background: 'var(--surface,#fff)', color: 'inherit', fontSize: 15, marginBottom: 10, cursor: 'pointer' }}>← Revenir au dernier</button>
        <button onClick={close} style={{ padding: '14px 28px', border: 'none', borderRadius: 12, background: O, color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>Fermer</button>
      </div>
    );
  }

  // ── Prise de vue ────────────────────────────────────────────────
  const cur = queue[idx];
  const btn = { background: 'rgba(255,255,255,.18)', color: '#fff', border: 'none', borderRadius: 12, padding: '12px 16px', fontSize: 15, fontWeight: 600, cursor: 'pointer', minWidth: 84 };
  return (
    <div style={wrap}>
      <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile} style={{ display: 'none' }} />
      <video ref={videoRef} playsInline muted autoPlay style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', background: '#111' }} />
      {flash && <div style={{ position: 'absolute', inset: 0, background: '#fff', opacity: .8 }} />}

      <div style={{ position: 'relative', padding: 'calc(12px + env(safe-area-inset-top)) 14px 14px', background: 'linear-gradient(#000c,#0000)', color: '#fff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={close} style={{ ...btn, minWidth: 0, padding: '8px 12px' }}>✕ Fermer</button>
          <div style={{ fontSize: 13, opacity: .9 }}>{idx + 1} / {queue.length}{pending > 0 ? ` · ⏫ ${pending}` : ''}{failed > 0 ? ` · ⚠️ ${failed}` : ''}</div>
        </div>
        <div style={{ height: 4, background: '#fff3', borderRadius: 9, margin: '10px 0' }}>
          <div style={{ height: '100%', width: `${(idx / queue.length) * 100}%`, background: O, borderRadius: 9, transition: 'width .2s' }} />
        </div>
        <div style={{ fontFamily: D, fontSize: '1.7rem', letterSpacing: '.04em', lineHeight: 1.1 }}>{cur.label}</div>
        <div style={{ fontSize: 13, opacity: .85 }}>{cur.serie}{taken[cur.id] ? ` · ✓ ${taken[cur.id]} photo${taken[cur.id] > 1 ? 's' : ''}` : ''}</div>
        {camErr && <div style={{ fontSize: 12, marginTop: 6, color: '#FDE68A' }}>{camErr}</div>}
      </div>

      <div style={{ flex: 1 }} />

      <div style={{ position: 'relative', padding: '14px 14px calc(18px + env(safe-area-inset-bottom))', background: 'linear-gradient(#0000,#000c)', color: '#fff' }}>
        <label style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 12 }}>
          <input type="checkbox" checked={auto} onChange={e => setAuto(e.target.checked)} style={{ width: 16, height: 16 }} />
          Passer automatiquement à l'album suivant
        </label>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={() => setIdx(i => Math.max(0, i - 1))} disabled={idx === 0} style={{ ...btn, opacity: idx === 0 ? .35 : 1 }}>← Préc.</button>
          <button onClick={shoot} aria-label="Prendre la photo"
            style={{ width: 78, height: 78, borderRadius: '50%', border: '5px solid #fff', background: O, cursor: 'pointer', boxShadow: '0 2px 12px #0008' }} />
          <button onClick={() => setIdx(i => i + 1)} style={btn}>{idx === queue.length - 1 ? 'Terminer' : 'Passer →'}</button>
        </div>
      </div>
    </div>
  );
}
